/**
 * Claude Code message parser
 *
 * Extracts Claude-specific parsing logic from messageGroups.ts.
 * Converts raw or normalized Claude Code provider events into MessageGroup[] for rendering.
 */

import { normalizeClaudeProviderEventsWithSources } from "../../../providerEvents/claude"
import type {
    ClaudeAssistantEvent,
    ClaudeContentBlock,
    ClaudeEvent,
    ClaudeRawJsonEvent,
    ClaudeResultEvent,
    ClaudeUserContentBlock,
    ClaudeUserEvent,
} from "../../../providerEvents/claude"
import type { ActivityEvent, MessageGroup, ProviderNoticeGroup, ResultGroup, SystemGroup, TodoItem, ToolGroup } from "../messageGroups"
import { extractClaudeAgentActivity } from "./agentActivity"

/** Extract text content from assistant message */
function getAssistantText(msg: ClaudeEvent): string | null {
    if (msg.type !== "assistant") return null
    const { content } = (msg as ClaudeAssistantEvent).message
    if (!Array.isArray(content)) return null
    const text = content
        .filter((block): block is ClaudeContentBlock & { type: "text" } => block.type === "text")
        .map((block) => block.text)
        .join("")
    return text.trim() || null
}

/** Extract thinking content from assistant message */
function getThinkingText(msg: ClaudeEvent): string | null {
    if (msg.type !== "assistant") return null
    const { content } = (msg as ClaudeAssistantEvent).message
    if (!Array.isArray(content)) return null
    const text = content
        .filter((block): block is ClaudeContentBlock & { type: "thinking" } => block.type === "thinking")
        .map((block) => block.thinking)
        .join("")
    return text.trim() || null
}

/** Extract tool_use from assistant message */
function getToolUse(msg: ClaudeEvent): { id: string; name: string; input: Record<string, unknown> } | null {
    if (msg.type !== "assistant") return null
    const { content } = (msg as ClaudeAssistantEvent).message
    if (!Array.isArray(content)) return null
    const toolUseBlock = content.find((block): block is ClaudeContentBlock & { type: "tool_use" } => block.type === "tool_use")
    if (!toolUseBlock) return null
    return {
        id: toolUseBlock.id,
        name: toolUseBlock.name,
        input: toolUseBlock.input,
    }
}

/** Extract tool_result from user message */
function getToolResult(msg: ClaudeEvent): { toolUseId: string; content: string; isError: boolean } | null {
    if (msg.type !== "user") return null
    const { content } = (msg as ClaudeUserEvent).message
    if (!Array.isArray(content)) return null

    const toolResultBlock = content.find((block): block is ClaudeUserContentBlock & { type: "tool_result" } => block.type === "tool_result")
    if (!toolResultBlock) return null

    // Content can be a string or array of content blocks
    let contentStr: string
    if (typeof toolResultBlock.content === "string") {
        contentStr = toolResultBlock.content
    } else if (Array.isArray(toolResultBlock.content)) {
        contentStr = toolResultBlock.content
            .map((c: unknown) => {
                const block = c as { type: string; text?: string }
                return block.type === "text" ? block.text || "" : JSON.stringify(c)
            })
            .join("\n")
    } else {
        contentStr = JSON.stringify(toolResultBlock.content)
    }

    return {
        toolUseId: toolResultBlock.tool_use_id,
        content: contentStr,
        isError: toolResultBlock.is_error === true,
    }
}

/**
 * Group Claude Code messages into MessageGroups for inline rendering.
 *
 * This is the Claude-specific parser invoked by the harness-agnostic
 * groupStreamEvents in messageGroups.ts.
 */
export function groupClaudeCodeProviderEvents(messages: unknown[]): MessageGroup[] {
    const normalized = normalizeClaudeProviderEventsWithSources(messages)
    const claudeEvents = normalized.events
    const groups: MessageGroup[] = []
    const agentActivity = extractClaudeAgentActivity(claudeEvents)

    // Build a map of tool_use_id -> user message index for quick lookup
    const toolResultMap = new Map<string, { index: number; content: string; isError: boolean }>()
    for (let i = 0; i < claudeEvents.length; i++) {
        const result = getToolResult(claudeEvents[i])
        if (result) {
            toolResultMap.set(result.toolUseId, {
                index: i,
                content: result.content,
                isError: result.isError,
            })
        }
    }

    // Peak thinking-token estimate seen since the last thinking group; folded into the next one.
    let pendingThinkingTokens: number | null = null

    // Process messages in order
    for (let i = 0; i < claudeEvents.length; i++) {
        const msg = claudeEvents[i]

        if (agentActivity.group?.messageIndex === i) {
            groups.push(agentActivity.group)
        }
        if (agentActivity.consumedIndices.has(i)) continue

        const thinkingTokens = readThinkingTokensEvent(msg)
        if (thinkingTokens) {
            if (thinkingTokens.estimate !== null) {
                pendingThinkingTokens = Math.max(pendingThinkingTokens ?? 0, thinkingTokens.estimate)
            }
            continue
        }

        const providerNotice = claudeProviderNotice(msg, i)
        if (providerNotice === "hidden") continue
        if (providerNotice) {
            groups.push(providerNotice)
            continue
        }

        const webSearchGroup = getWebSearchToolGroup(msg, i)
        if (webSearchGroup) {
            groups.push(webSearchGroup)
            continue
        }

        const rawJsonSystemGroup = getRawJsonVisibleSystemGroup(msg, i)
        if (rawJsonSystemGroup) {
            groups.push(rawJsonSystemGroup)
            continue
        }

        // Handle system messages
        if (msg.type === "system") {
            const subtype = msg.subtype as SystemGroup["subtype"] | string

            // Skip status messages when status is null (they're meaningless)
            if (subtype === "status" && "status" in msg && msg.status === null) {
                continue
            }

            // Only include specific subtypes we want to display
            if (isVisibleSystemSubtype(subtype)) {
                const { type: _type, subtype: _subtype, ...metadata } = msg as Record<string, unknown>
                groups.push({
                    type: "system",
                    subtype,
                    metadata,
                    messageIndex: i,
                })
            } else {
                pushActivityEvent(groups, claudeActivityEvent(msg, i, `system:${subtype}`, `System: ${subtype.replace(/_/g, " ")}`))
            }
            continue
        }

        // Handle result messages
        if (msg.type === "result") {
            // Turn boundary: a pending estimate with no thinking group to attach to is stale.
            pendingThinkingTokens = null
            const resultMsg = msg as ClaudeResultEvent
            const isError = resultMsg.is_error === true || (typeof resultMsg.subtype === "string" && resultMsg.subtype.startsWith("error"))
            groups.push({
                type: "result",
                subtype: claudeResultSubtype(resultMsg, isError),
                durationMs: resultMsg.duration_ms,
                totalCostUsd: resultMsg.total_cost_usd,
                usage: {
                    inputTokens: (resultMsg.usage as { input_tokens?: number })?.input_tokens ?? 0,
                    outputTokens: (resultMsg.usage as { output_tokens?: number })?.output_tokens ?? 0,
                },
                isError,
                result: resultMsg.result,
                errors: (resultMsg as unknown as { errors?: string[] }).errors,
                messageIndex: i,
            })
            continue
        }

        // Skip user messages (they're handled via tool_result pairing)
        if (msg.type === "user") {
            continue
        }

        if (msg.type === "relocated" || msg.type === "worktree-state") {
            continue
        }

        // Handle assistant messages
        if (msg.type === "assistant") {
            const groupCountBeforeAssistant = groups.length
            // Fold any buffered thinking-token estimate into this turn's thinking group, then clear
            // it so it cannot leak into a later turn that happens to have no thinking of its own.
            const estimatedThinkingTokens = pendingThinkingTokens ?? undefined
            pendingThinkingTokens = null
            const thinking = getThinkingText(msg)
            const text = getAssistantText(msg)
            const toolUse = getToolUse(msg)
            const unknownContentBlocks = getUnknownAssistantContentBlocks(msg)

            // If has thinking, create thinking group (before text)
            if (thinking) {
                groups.push({
                    type: "thinking",
                    text: thinking,
                    estimatedThinkingTokens,
                    messageIndex: i,
                })
            }

            // If has text, create text group
            if (text) {
                groups.push({
                    type: "text",
                    text,
                    messageIndex: i,
                })
            }

            // If has tool use, create appropriate group
            if (toolUse) {
                const result = toolResultMap.get(toolUse.id)

                // Check if this is an Edit tool call
                if (agentActivity.agentToolUseIds.has(toolUse.id)) {
                    // Agent calls are rendered together as one live activity group.
                } else if (toolUse.name === "Edit") {
                    const input = toolUse.input as {
                        file_path?: string
                        old_string?: string
                        new_string?: string
                    }
                    groups.push({
                        type: "edit",
                        toolUseId: toolUse.id,
                        filePath: input.file_path || "unknown",
                        oldString: input.old_string || "",
                        newString: input.new_string || "",
                        isError: result?.isError ?? false,
                        isPending: result === undefined,
                        errorMessage: result?.isError ? result.content : undefined,
                        messageIndices: [i, result?.index],
                    })
                } else if (toolUse.name === "Write") {
                    // Write tool call - render as diff (empty -> content)
                    const input = toolUse.input as {
                        file_path?: string
                        content?: string
                    }
                    groups.push({
                        type: "write",
                        toolUseId: toolUse.id,
                        filePath: input.file_path || "unknown",
                        content: input.content || "",
                        isError: result?.isError ?? false,
                        isPending: result === undefined,
                        errorMessage: result?.isError ? result.content : undefined,
                        messageIndices: [i, result?.index],
                    })
                } else if (toolUse.name === "Bash") {
                    // Bash tool call - render as prompt/response
                    const input = toolUse.input as {
                        command?: string
                        description?: string
                    }
                    groups.push({
                        type: "bash",
                        toolUseId: toolUse.id,
                        command: input.command || "",
                        description: input.description,
                        result: result?.content,
                        isError: result?.isError ?? false,
                        isPending: result === undefined,
                        messageIndices: [i, result?.index],
                    })
                } else if (toolUse.name === "TodoWrite") {
                    // TodoWrite tool call - render as todo list with completion visualization
                    const input = toolUse.input as {
                        todos?: Array<{
                            content?: string
                            status?: "pending" | "in_progress" | "completed"
                            activeForm?: string
                        }>
                    }
                    const todos: TodoItem[] = (Array.isArray(input.todos) ? input.todos : []).map((t) => ({
                        content: t.content || "",
                        status: t.status || "pending",
                        activeForm: t.activeForm || t.content || "",
                    }))
                    groups.push({
                        type: "todoWrite",
                        toolUseId: toolUse.id,
                        todos,
                        isError: result?.isError ?? false,
                        isPending: result === undefined,
                        messageIndices: [i, result?.index],
                    })
                } else {
                    // Regular tool group
                    groups.push({
                        type: "tool",
                        toolUseId: toolUse.id,
                        toolName: toolUse.name,
                        input: toolUse.input,
                        result: result?.content,
                        isError: result?.isError ?? false,
                        messageIndices: [i, result?.index],
                    })
                }
            }

            if (unknownContentBlocks.length > 0) {
                if (unknownContentBlocks.every(isFallbackContentBlock)) {
                    pushActivityEvent(groups, {
                        harnessId: "claude-code",
                        label: "Model fallback marker",
                        originalType: "assistant.content:fallback",
                        summary: "Model fallback",
                        raw: unknownContentBlocks,
                        messageIndex: i,
                    })
                } else {
                    groups.push({
                        type: "unknown",
                        harnessId: "claude-code",
                        label: "Unknown Claude content",
                        originalType: "assistant.content",
                        raw: unknownContentBlocks,
                        messageIndex: i,
                    })
                }
            } else if (groups.length === groupCountBeforeAssistant && !toolUse) {
                if (hasEmptyThinkingBlock(msg)) {
                    groups.push({
                        type: "thinking",
                        text: "Thinking",
                        estimatedThinkingTokens,
                        messageIndex: i,
                    })
                } else if (!hasOnlyEmptyAssistantContent(msg)) {
                    pushClaudeUnknownGroup(groups, msg, i)
                }
            }

            continue
        }

        const activity = claudeActivityForUnhandledEvent(msg, i)
        if (activity) {
            pushActivityEvent(groups, activity)
            continue
        }

        pushClaudeUnknownGroup(groups, msg, i)
    }

    assignSourceMessageIndices(groups, normalized.sourceIndices)
    return groups
}

function assignSourceMessageIndices(groups: MessageGroup[], sourceIndices: readonly number[]): void {
    for (const group of groups) {
        const index =
            group.type === "tool" ||
            group.type === "edit" ||
            group.type === "write" ||
            group.type === "bash" ||
            group.type === "orchestration" ||
            group.type === "todoWrite"
                ? group.messageIndices[0]
                : group.type === "stderr"
                  ? -1
                  : group.messageIndex
        if (index >= 0) {
            Object.defineProperty(group, "sourceMessageIndex", {
                configurable: true,
                value: sourceIndices[index] ?? index,
            })
        }
    }
}

function claudeResultSubtype(result: ClaudeResultEvent, isError: boolean): ResultGroup["subtype"] {
    if (!isError) return "success"
    switch (result.subtype) {
        case "error_max_turns":
        case "error_max_budget_usd":
        case "error_max_structured_output_retries":
            return result.subtype
        default:
            return "error_during_execution"
    }
}

function getWebSearchToolGroup(msg: ClaudeEvent, messageIndex: number): ToolGroup | null {
    const event = msg as unknown as { type?: unknown; original_type?: unknown; raw?: unknown }
    let raw: Record<string, unknown> | null = null

    if (event.type === "web_search") {
        raw = event as Record<string, unknown>
    } else if (event.type === "raw_json" && event.original_type === "web_search" && event.raw && typeof event.raw === "object") {
        raw = event.raw as Record<string, unknown>
    }

    if (!raw) return null

    const { type: _type, id: rawId, ...input } = raw
    const toolUseId = typeof rawId === "string" ? rawId : `web-search-${messageIndex}`
    return {
        type: "tool",
        toolUseId,
        toolName: "WebSearch",
        input,
        isError: false,
        messageIndices: [messageIndex, undefined],
    }
}

function getRawJsonVisibleSystemGroup(msg: ClaudeEvent, messageIndex: number): SystemGroup | null {
    if (msg.type !== "raw_json") return null
    if (msg.original_type !== "system" || !isVisibleSystemSubtype(msg.original_subtype)) return null

    const { type: _type, subtype: _subtype, ...metadata } = msg.raw
    return {
        type: "system",
        subtype: msg.original_subtype,
        metadata,
        messageIndex,
    }
}

function isVisibleSystemSubtype(subtype: string | undefined): subtype is SystemGroup["subtype"] {
    return (
        subtype === "compact_boundary" ||
        subtype === "status" ||
        subtype === "init" ||
        subtype === "hook_started" ||
        subtype === "hook_progress" ||
        subtype === "hook_response" ||
        subtype === "api_retry" ||
        subtype === "task_started" ||
        subtype === "task_progress" ||
        subtype === "task_notification" ||
        subtype === "task_updated"
    )
}

/**
 * thinking_tokens is a streaming estimate the CLI emits roughly every ~100 thinking tokens while the
 * model reasons. It is not transcript content, and it is not a known SDK system subtype, so it
 * arrives as raw_json today (typed system form tolerated for forward-compat). Returns the matched
 * estimate so the caller can fold the peak into the thinking group it precedes; returns null when the
 * event is not a thinking_tokens event at all.
 */
function readThinkingTokensEvent(msg: ClaudeEvent): { estimate: number | null } | null {
    const raw = msg as unknown as {
        type?: unknown
        subtype?: unknown
        original_type?: unknown
        original_subtype?: unknown
        estimated_tokens?: unknown
        raw?: { estimated_tokens?: unknown }
    }
    const isTyped = raw.type === "system" && raw.subtype === "thinking_tokens"
    const isRawJson = raw.type === "raw_json" && raw.original_type === "system" && raw.original_subtype === "thinking_tokens"
    if (!isTyped && !isRawJson) return null

    const value = isTyped ? raw.estimated_tokens : raw.raw?.estimated_tokens
    return { estimate: typeof value === "number" && Number.isFinite(value) ? value : null }
}

function getUnknownAssistantContentBlocks(msg: ClaudeEvent): unknown[] {
    if (msg.type !== "assistant") return []
    const { content } = msg.message
    if (!Array.isArray(content)) return []
    return content.filter((block) => {
        const type = (block as { type?: unknown }).type
        return type !== "text" && type !== "thinking" && type !== "tool_use"
    })
}

function isFallbackContentBlock(block: unknown): boolean {
    return typeof block === "object" && block !== null && !Array.isArray(block) && (block as { type?: unknown }).type === "fallback"
}

function hasOnlyEmptyAssistantContent(msg: ClaudeEvent): boolean {
    if (msg.type !== "assistant") return false
    const { content } = msg.message
    if (!Array.isArray(content)) return false
    if (content.length === 0) return true

    return content.every((block) => {
        const raw = block as { type?: unknown; text?: unknown; thinking?: unknown }
        if (raw.type === "text") return typeof raw.text !== "string" || raw.text.trim() === ""
        if (raw.type === "thinking") return typeof raw.thinking !== "string" || raw.thinking.trim() === ""
        return false
    })
}

function hasEmptyThinkingBlock(msg: ClaudeEvent): boolean {
    if (msg.type !== "assistant") return false
    const { content } = msg.message
    if (!Array.isArray(content)) return false
    return content.some((block) => {
        const raw = block as { type?: unknown; thinking?: unknown }
        return raw.type === "thinking" && (typeof raw.thinking !== "string" || raw.thinking.trim() === "")
    })
}

function pushClaudeUnknownGroup(groups: MessageGroup[], msg: ClaudeEvent, messageIndex: number): void {
    const rawMsg = msg as unknown as Record<string, unknown>
    const rawJson = msg.type === "raw_json" ? (msg as ClaudeRawJsonEvent) : null
    const originalType = rawJson ? formatRawJsonType(rawJson) : formatClaudeType(rawMsg)

    groups.push({
        type: "unknown",
        harnessId: "claude-code",
        label: `Unknown Claude event: ${originalType ?? "event"}`,
        originalType,
        raw: rawJson?.raw ?? msg,
        messageIndex,
    })
}

const CLAUDE_ACTIVITY_TOP_TYPES = new Set<string>([
    "agent-name",
    "rate_limit_event",
    "tool_progress",
    "tool_use_summary",
    "auth_status",
    "prompt_suggestion",
    "started",
])

const CLAUDE_HIDDEN_TOP_TYPES = new Set([
    "custom-title",
    "ai-title",
    "last-prompt",
    "queue-operation",
    "mode",
    "file-history-snapshot",
    "stream_event",
    "atis-latch",
])

const CLAUDE_HIDDEN_ATTACHMENT_TYPES = new Set([
    "deferred_tools_delta",
    "task_reminder",
    "skill_listing",
    "nested_memory",
    "agent_listing_delta",
    "edited_text_file",
    "dynamic_skill",
    "command_permissions",
    "compact_file_reference",
    "todo_reminder",
    "date_change",
    "already_read_file",
    "workflow_keyword_request",
])

function claudeProviderNotice(msg: ClaudeEvent, messageIndex: number): ProviderNoticeGroup | "hidden" | null {
    const rawJson = msg.type === "raw_json" ? (msg as ClaudeRawJsonEvent) : null
    const raw = recordValue(rawJson?.raw ?? msg)
    if (!raw) return null
    const originalType = rawJson?.original_type ?? stringValue(raw.type)
    const subtype = rawJson?.original_subtype ?? stringValue(raw.subtype)

    if (originalType && CLAUDE_HIDDEN_TOP_TYPES.has(originalType)) return "hidden"

    if (originalType === "attachment") {
        const attachment = recordValue(raw.attachment)
        const attachmentType = stringValue(attachment?.type)
        if (!attachmentType) return null
        if (CLAUDE_HIDDEN_ATTACHMENT_TYPES.has(attachmentType)) return "hidden"

        if (attachmentType === "plan_mode" || attachmentType === "plan_mode_exit") {
            const path = stringValue(attachment?.planFilePath) ?? stringValue(attachment?.plan_file_path)
            return {
                type: "providerNotice",
                harnessId: "claude-code",
                kind: attachmentType,
                title: attachmentType === "plan_mode" ? "Entered plan mode" : "Plan ready",
                ...(path ? { body: path } : {}),
                level: attachmentType === "plan_mode" ? "info" : "success",
                metadata: optionalMetadata({ planExists: attachment?.planExists }),
                messageIndex,
            }
        }

        if (attachmentType === "read_truncation_notice") {
            return {
                type: "providerNotice",
                harnessId: "claude-code",
                kind: "read_truncation",
                title: "Tool output truncated",
                ...(stringValue(attachment?.banner) ? { body: stringValue(attachment?.banner) } : {}),
                level: "warning",
                metadata: optionalMetadata({ toolUseId: attachment?.toolUseID ?? attachment?.tool_use_id }),
                messageIndex,
            }
        }
        return null
    }

    if (originalType === "pr-link") {
        const number = finiteNumber(raw.prNumber)
        const repository = stringValue(raw.prRepository)
        const url = safeHttpUrl(raw.prUrl)
        return {
            type: "providerNotice",
            harnessId: "claude-code",
            kind: "pull_request",
            title: number === undefined ? "Pull request" : `Pull request #${number}`,
            ...(repository ? { body: repository } : {}),
            ...(url ? { url } : {}),
            level: "success",
            metadata: optionalMetadata({ repository, number }),
            messageIndex,
        }
    }

    if (originalType === "permission-mode") {
        const mode = stringValue(raw.permissionMode) ?? stringValue(raw.mode)
        return {
            type: "providerNotice",
            harnessId: "claude-code",
            kind: "permission_mode",
            title: mode ? `Permission mode: ${mode}` : "Permission mode changed",
            level: "info",
            metadata: optionalMetadata({ mode }),
            messageIndex,
        }
    }

    if (originalType !== "system") return null
    if (subtype === "turn_duration") return "hidden"

    if (subtype === "api_error") {
        const error = recordValue(raw.error)
        const connection = recordValue(error?.connection)
        const attempt = finiteNumber(raw.retryAttempt) ?? finiteNumber(raw.attempt)
        const maxRetries = finiteNumber(raw.maxRetries) ?? finiteNumber(raw.max_retries)
        const retryInMs = finiteNumber(raw.retryInMs) ?? finiteNumber(raw.retry_delay_ms)
        const isFinal = attempt !== undefined && maxRetries !== undefined && attempt >= maxRetries
        const body = stringValue(error?.formatted) ?? stringValue(error?.message) ?? stringValue(connection?.message)
        return {
            type: "providerNotice",
            harnessId: "claude-code",
            kind: "api_error",
            title: "Claude connection error",
            ...(body ? { body } : {}),
            level: isFinal ? "error" : "warning",
            metadata: optionalMetadata({ attempt, maxRetries, retryInMs }),
            messageIndex,
        }
    }

    if (subtype === "model_refusal_fallback") {
        const originalModel = stringValue(raw.originalModel)
        const fallbackModel = stringValue(raw.fallbackModel)
        const body = modelFallbackBody(originalModel, fallbackModel)
        return {
            type: "providerNotice",
            harnessId: "claude-code",
            kind: "model_fallback",
            title: "Model fallback",
            ...(body ? { body } : {}),
            level: "warning",
            metadata: optionalMetadata({ originalModel, fallbackModel }),
            messageIndex,
        }
    }

    if (subtype === "away_summary") {
        return {
            type: "providerNotice",
            harnessId: "claude-code",
            kind: "recap",
            title: "Away recap",
            ...(stringValue(raw.content) ? { body: stringValue(raw.content) } : {}),
            level: "info",
            metadata: {},
            messageIndex,
        }
    }

    if (subtype === "agents_killed") {
        return {
            type: "providerNotice",
            harnessId: "claude-code",
            kind: "agents_killed",
            title: "Agents stopped",
            level: "warning",
            metadata: {},
            messageIndex,
        }
    }

    if (subtype === "informational") {
        const level = stringValue(raw.level)
        return {
            type: "providerNotice",
            harnessId: "claude-code",
            kind: "informational",
            title: "Claude notice",
            ...(stringValue(raw.content) ? { body: stringValue(raw.content) } : {}),
            level: informationalNoticeLevel(level),
            metadata: optionalMetadata({ level }),
            messageIndex,
        }
    }

    return null
}

function modelFallbackBody(originalModel: string | undefined, fallbackModel: string | undefined): string | undefined {
    if (originalModel && fallbackModel) return `${originalModel} → ${fallbackModel}`
    return fallbackModel ?? originalModel
}

function informationalNoticeLevel(level: string | undefined): ProviderNoticeGroup["level"] {
    switch (level) {
        case "error":
            return "error"
        case "warning":
            return "warning"
        default:
            return "info"
    }
}

function optionalMetadata(values: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(values).filter((entry) => entry[1] !== undefined && entry[1] !== null && entry[1] !== ""))
}

function finiteNumber(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

function safeHttpUrl(value: unknown): string | undefined {
    const url = stringValue(value)
    return url && (url.startsWith("https://") || url.startsWith("http://")) ? url : undefined
}

function claudeActivityForUnhandledEvent(msg: ClaudeEvent, messageIndex: number): ActivityEvent | null {
    const rawJson = msg.type === "raw_json" ? (msg as ClaudeRawJsonEvent) : null
    const raw = rawJson?.raw ?? (msg as unknown as Record<string, unknown>)
    const originalType = rawJson ? rawJson.original_type : typeof raw.type === "string" ? raw.type : undefined

    if (rawJson?.original_type === "attachment") {
        const attachment = recordValue(raw.attachment)
        const attachmentType = stringValue(attachment?.type) ?? "attachment"
        return claudeActivityEvent(rawJson, messageIndex, `attachment:${attachmentType}`, `Attachment: ${attachmentType.replace(/_/g, " ")}`)
    }

    if (rawJson?.original_type === "system") {
        const subtype = rawJson.original_subtype ?? "system"
        return claudeActivityEvent(rawJson, messageIndex, `system:${subtype}`, `System: ${subtype.replace(/_/g, " ")}`)
    }

    if (originalType && CLAUDE_ACTIVITY_TOP_TYPES.has(originalType)) {
        return claudeActivityEvent(msg, messageIndex, originalType, claudeActivityLabel(originalType))
    }

    return null
}

function claudeActivityEvent(msg: ClaudeEvent, messageIndex: number, originalType: string, label: string): ActivityEvent {
    const rawJson = msg.type === "raw_json" ? (msg as ClaudeRawJsonEvent) : null
    const raw = rawJson?.raw ?? msg
    return {
        harnessId: "claude-code",
        label,
        originalType,
        summary: claudeActivitySummary(raw, originalType),
        raw,
        messageIndex,
    }
}

function claudeActivityLabel(originalType: string): string {
    switch (originalType) {
        case "agent-name":
            return "Agent name"
        case "rate_limit_event":
            return "Rate limit"
        case "tool_progress":
            return "Tool progress"
        case "tool_use_summary":
            return "Tool summary"
        case "started":
            return "Workflow started"
        default:
            return originalType.replace(/_/g, " ")
    }
}

function claudeActivitySummary(raw: unknown, originalType: string): string | undefined {
    const record = recordValue(raw)
    if (!record) return undefined
    const attachment = recordValue(record.attachment)
    const error = recordValue(record.error)
    const connection = recordValue(error?.connection)
    const itemCount = typeof attachment?.itemCount === "number" ? attachment.itemCount : undefined
    const durationMs = typeof record.durationMs === "number" ? record.durationMs : undefined
    const originalModel = stringValue(record.originalModel)
    const fallbackModel = stringValue(record.fallbackModel)
    const pieces = [
        stringValue(record.operation),
        stringValue(record.mode),
        stringValue(record.permissionMode),
        stringValue(record.agentName),
        stringValue(record.aiTitle),
        stringValue(record.lastPrompt),
        typeof record.prNumber === "number" ? `#${record.prNumber}` : undefined,
        stringValue(record.prRepository),
        stringValue(record.key),
        stringValue(record.agentId),
        stringValue(attachment?.displayPath),
        stringValue(attachment?.filename),
        itemCount !== undefined ? `${itemCount} item${itemCount === 1 ? "" : "s"}` : undefined,
        durationMs !== undefined ? `${Math.round(durationMs / 1000)}s` : undefined,
        fallbackModel && originalModel ? `${originalModel} -> ${fallbackModel}` : undefined,
        stringValue(error?.message),
        stringValue(connection?.message),
    ].filter((piece): piece is string => Boolean(piece))
    if (pieces.length > 0) return pieces.join(" · ")
    return originalType
}

function pushActivityEvent(groups: MessageGroup[], event: ActivityEvent): void {
    const previous = groups[groups.length - 1]
    if (previous?.type === "activity" && previous.harnessId === event.harnessId) {
        const existing = previous.events.find((item) => item.originalType === event.originalType && item.summary === event.summary)
        if (existing) {
            existing.count = (existing.count ?? 1) + (event.count ?? 1)
            existing.raw = event.raw
            return
        }
        previous.events.push(event)
        return
    }

    groups.push({
        type: "activity",
        harnessId: event.harnessId,
        events: [event],
        messageIndex: event.messageIndex,
    })
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function formatRawJsonType(event: ClaudeRawJsonEvent): string | undefined {
    if (event.original_type === "system" && event.original_subtype) return `system:${event.original_subtype}`
    return event.original_type
}

function formatClaudeType(raw: Record<string, unknown>): string | undefined {
    const type = typeof raw.type === "string" ? raw.type : undefined
    const subtype = typeof raw.subtype === "string" ? raw.subtype : undefined
    if (type === "system" && subtype) return `system:${subtype}`
    return type
}
